import { useState } from 'react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { Button } from '@/components/ui/button'
import { useToast } from '@/providers/UiProviders'
import { DangerDialog, type DangerLevel, type ImpactData } from '@/components/DangerDialog'

/**
 * X2 tekshiruv sahifasi (`/dev/danger`).
 *
 * NIMA UCHUN ALOHIDA SAHIFA: X2 — Faza 1 deliverabli, lekin uning
 * iste'molchilari (A3.27 cheklov, A3.28 bloklash, A10.13 refund, A11.04
 * davlat yopish) Faza 2–5 da keladi. Naqshni HOZIR sinab ko'rish uchun
 * hech qanday API ni chaqirmaydigan sahifa kerak.
 *
 * Bu sahifa navigatsiyada YO'Q — faqat to'g'ridan-to'g'ri havola bilan.
 */

const IMPACT: ImpactData = {
  items: [
    { label: 'obunachi kirishni yo\'qotadi', value: 48392 },
    { label: 'xabar o\'chiriladi', value: 12847 },
    { label: 'ulangan bot uziladi', value: 3 },
    { label: 'oylik monetizatsiya to\'xtaydi', value: '$1,240', emphasis: true },
  ],
  reversible: '7 kun',
}

/** Sekin hisoblanadigan ta'sir — skeleton holatini tekshirish uchun. */
function slowImpact(): Promise<ImpactData> {
  return new Promise((resolve) => setTimeout(() => resolve(IMPACT), 1800))
}

export function DangerDemoScreen() {
  const t = useT()
  const { toast } = useToast()
  const [level, setLevel] = useState<DangerLevel | null>(null)

  const specs: Record<DangerLevel, { title: string; confirm: string; object?: string }> = {
    1: { title: 'Ichki izohni o\'chirish', confirm: t.common.delete },
    2: { title: 'Foydalanuvchini 24 soatga cheklash', confirm: 'Cheklash' },
    3: { title: 'Kanalni o\'chirish', confirm: t.common.delete, object: '@grom_official' },
    4: { title: 'Yechishni tasdiqlash', confirm: 'Tasdiqlash', object: 'PAY-99213' },
    5: { title: 'Shifrlash kalitini eksport qilish', confirm: 'Eksport', object: 'kms-root-2026' },
  }

  return (
    <>
      <PageHeader
        title="Xavfli amallar (X2)"
        screenId="X2"
        description="5 daraja — har birini bosib tekshiring. Hech qanday API chaqirilmaydi."
      />

      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid max-w-4xl gap-2">
          {([1, 2, 3, 4, 5] as DangerLevel[]).map((l) => (
            <div
              key={l}
              className="flex items-center gap-3 rounded-md border border-border-default bg-surface-2 p-3"
            >
              <span className="w-20 shrink-0 font-mono text-sm text-tertiary">
                {t.danger.level(l)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-base text-primary">{t.danger.levelName[l]}</p>
                <p className="text-sm text-secondary">{specs[l].title}</p>
              </div>
              <Button
                variant={l >= 5 ? 'critical' : l >= 3 ? 'danger' : l === 2 ? 'warning' : 'default'}
                onClick={() => setLevel(l)}
              >
                {specs[l].confirm}
              </Button>
            </div>
          ))}
        </div>

        <p className="mt-4 max-w-2xl text-sm text-secondary">
          4–5 darajalarda tasdiq tugmasi ATAYLAB o'chiq: four-eyes sxemasi
          tasdiqlandi va yozildi (migratsiya{' '}
          <span className="font-mono text-xs">000076_four_eyes_requests</span>), lekin
          uni o'qiydigan/yozadigan endpoint hali yo'q. Endpoint kelgach{' '}
          <span className="font-mono text-xs">onCreateRequest</span> ulanadi —{' '}
          <span className="font-mono text-xs">tz/admin/HOLAT.md §2-P0</span>.
        </p>
      </div>

      {level !== null && (
        <DangerDialog
          open
          onClose={() => setLevel(null)}
          screenId="X2"
          level={level}
          title={specs[level].title}
          confirmLabel={specs[level].confirm}
          {...(specs[level].object ? { objectName: specs[level].object } : {})}
          {...(level >= 3 ? { impact: level === 3 ? slowImpact : IMPACT } : {})}
          reasonPresets={['Spam', 'Firibgarlik', 'Qoidabuzarlik', 'Huquqiy so\'rov']}
          onConfirm={async ({ reason }) => {
            toast(`${t.danger.levelName[level]}: bajarildi`, {
              tone: 'success',
              ...(reason ? { description: reason } : {}),
              // 1-daraja qaytarib bo'ladi → undo toast 10 s (SKILL §13)
              ...(level === 1
                ? { undo: () => toast(t.danger.undoneToast, { tone: 'info' }) }
                : {}),
            })
          }}
        />
      )}
    </>
  )
}
